import { CertificateWorkshopSet } from '../entities/certificate-workshop-set.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getCertificateWorkshopSetsByIDs = registerQuery({
  operationName: 'getCertificateWorkshopSetsByIDs',
  type: () => [CertificateWorkshopSet],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) =>
      readEntitiesByIDs(CertificateWorkshopSet, ids, gqlFields),
});

export const subscribeCertificateWorkshopSetsByIDs = registerSubscription({
  operationName: 'subscribeCertificateWorkshopSetsByIDs',
  type: () => [CertificateWorkshopSet],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(CertificateWorkshopSet, ids, gqlFields),
});

export const getAllCertificateWorkshopSets = registerQuery({
  operationName: 'getAllCertificateWorkshopSets',
  type: () => [CertificateWorkshopSet],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) =>
      readAllEntities(CertificateWorkshopSet, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllCertificateWorkshopSets = registerSubscription({
  operationName: 'subscribeAllCertificateWorkshopSets',
  type: () => [CertificateWorkshopSet],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) =>
      watchAllEntities(CertificateWorkshopSet, gqlFields, { skip, limit, excludeIDs }),
});

export const getCertificateWorkshopSetByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getCertificateWorkshopSetsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeCertificateWorkshopSetByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<CertificateWorkshopSet>>(gqlFields: SelSet) =>
    id ? subscribeCertificateWorkshopSetsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
